import { ComponentProps } from 'react';
import { WithEnterpriseId } from '@mews-commander/types';
import { EnterpriseLink, EnterprisePaths } from '@mews-commander/ui';
import { createHandler } from '@mews/std';
import { OnboardingAuditArea, OnboardingAuditField, OnboardingAuditFieldIdentifier } from '@mews-commander/data-access';

type LinkProps<To extends EnterprisePaths, From extends string> = Pick<
    ComponentProps<typeof EnterpriseLink<To, From>>,
    'to' | 'params' | 'search'
>;

/* Temporary work around, remove when packages can consume route types */
type CastedWeakProps = LinkProps<EnterprisePaths, string>;

/** Factory function to create props for a route, we are casting the return type due to limitations with consuming router types from a package,
 *  when these limitations are overcome we can remove it and perhaps use EnterpriseLink more directly as well.
 *
 * Only for use in the context of TaskLink's internal nameToRouteInfo handler
 */
const props = <To extends EnterprisePaths, From extends string>(linkProps: LinkProps<To, From>): CastedWeakProps =>
    linkProps as unknown as CastedWeakProps;

const ACCOUNTING_CONFIG = ({ enterpriseId }: WithEnterpriseId) =>
    props({
        to: 'AccountingConfiguration/Detail/$enterpriseId',
        params: { enterpriseId },
    });

const ACCOUNTING_CATEGORIES = ({ enterpriseId }: WithEnterpriseId) =>
    props({
        to: 'AccountingConfiguration/AccountingCategories/$enterpriseId',
        params: { enterpriseId },
    });

const ENTERPRISE_DETAIL = ({ enterpriseId }: WithEnterpriseId) =>
    props({
        to: 'Enterprise/Detail/$enterpriseId',
        params: { enterpriseId },
    });

const SERVICES = ({ enterpriseId }: WithEnterpriseId) =>
    props({
        to: 'Service/Index',
        search: {
            OwnerId: enterpriseId,
        },
    });

const USER_MANAGEMENT = () =>
    props({
        to: 'UserManagement/PropertyUsers/Index',
    });

const RESOURCES = ({ enterpriseId }: WithEnterpriseId) =>
    props({
        to: 'Resource/Index',
        search: {
            OwnerId: enterpriseId,
        },
    });

export const auditToLinkProps = createHandler<
    OnboardingAuditFieldIdentifier,
    ((data: WithEnterpriseId) => CastedWeakProps) | null
>(
    {
        AcceptedManualPaymentCardTypesSetUp: ACCOUNTING_CONFIG,
        AccountingCategoriesSetUp: ACCOUNTING_CATEGORIES,
        AccountingConfigurationSetUp: ACCOUNTING_CONFIG,
        AdditionalServiceAccountingCategoriesSetUp: SERVICES,
        AdditionalServiceProductAccountingCategoriesSetUp: SERVICES,
        AdditionalServiceProductTaxesSetUp: SERVICES,
        AdditionalServicesSetUp: SERVICES,
        BillAndInvoiceCountersConfigured: () => props({ to: 'Counter/Index' }),
        BillHeaderAndFooterSetUp: ACCOUNTING_CONFIG,
        BookableProductTaxesSetUp: SERVICES,
        BookableRatesSetup: SERVICES,
        BookableServiceProductAccountingCategoriesSetUp: SERVICES,
        BookableServiceProductCategoriesSetUp: SERVICES,
        BookableServiceProductsSetUp: SERVICES,
        BookableServicesAccountingSettingsSetUp: SERVICES,
        BookableServicesAutomaticNoShowCancellationDisabled: SERVICES,
        BookableServicesGeneralSettingsSetUp: SERVICES,
        BookingEnginesAdded: SERVICES,
        CancellationPoliciesSetUp: () => props({ to: 'Policies/Index' }),
        CashiersAssigned: ({ enterpriseId }) => props({ to: 'Cashier/Index', search: { OwnerId: enterpriseId } }),
        CompanyProfilesAdded: () => props({ to: 'CompanyReport/Index' }),
        DepartmentsAdded: ({ enterpriseId }) => props({ to: 'Department/Index', search: { OwnerId: enterpriseId } }),
        EhwSetUp: ENTERPRISE_DETAIL,
        EmailTemplatesCustomized: ({ enterpriseId }) =>
            props({ to: 'MailTemplate/Index', search: { OwnerId: enterpriseId } }),
        EmployeeMfaSetup: ({ enterpriseId }) => props({ to: 'Security/Index', search: { OwnerId: enterpriseId } }),
        EmployeesAdded: USER_MANAGEMENT,
        EmployeesAssignedToDepartments: USER_MANAGEMENT,
        ExternalPaymentTypesSetUp: ACCOUNTING_CATEGORIES,
        GatewayPaymentTypesMapped: ACCOUNTING_CATEGORIES,
        MewsTerminalPaymentTypesMapped: ACCOUNTING_CATEGORIES,
        PaymentAutomationEnabled: () => props({ to: 'Policies/Index' }),
        ProductRulesSetUp: SERVICES,
        PropertyLogoAndSignInImageUploaded: ENTERPRISE_DETAIL,
        PropertySettingsCompleted: ENTERPRISE_DETAIL,
        QrCodesSetUp: SERVICES,
        RateGroupsSetUp: SERVICES,
        RequireAccountingCategorySetupActive: ACCOUNTING_CONFIG,
        RoutingRulesSetUp: () => props({ to: 'RoutingRule/Index' }),
        ServicePromotionsSetUp: SERVICES,
        SettlementRulesSetUp: SERVICES,
        SpaceCategoriesAdded: SERVICES,
        SpaceCategoriesAssigned: SERVICES,
        SpaceCategorySettingsSetUp: SERVICES,
        SpacesAdded: RESOURCES,
        SpacesAssigned: RESOURCES,
        TasksCompleted: () => props({ to: 'TaskReport/Index', search: { Filter: 'Deadline', 'State.Open': 'true' } }),
        TerminalPaymentTypesMapped: ACCOUNTING_CATEGORIES,
        TravelAgentContractsAdded: () => props({ to: 'TravelAgencyContractReport/Index' }),

        // No link available afaik, action is to talk to a Mews rep
        UnpaidInvoiceOverview: null,

        // Marketplace tasks, not used in Setup tab
        ChannelManagerConfigured: null,
        PrintersSetUp: null,
        KeyCuttersSetUp: null,
        TerminalsSetUp: null,
        AnalyticsSetUp: null,
        AccountingIntegrationsSetUp: null,
    },
    null
);

const financialAndSpacesOrder = [
    OnboardingAuditArea.PropertySettings,
    OnboardingAuditArea.FinancialSettings,
    OnboardingAuditArea.Users,
    OnboardingAuditArea.Spaces,
];

export const isFinancialAndSpaces = (area: OnboardingAuditArea) => financialAndSpacesOrder.includes(area);

export const compareFinancialAndSpacesOrder = (a: OnboardingAuditArea, b: OnboardingAuditArea) =>
    financialAndSpacesOrder.indexOf(a) - financialAndSpacesOrder.indexOf(b);

export const isServiceOrProduct = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.AdditionalServices || area === OnboardingAuditArea.BookableServices;

export const isRevenueManagement = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.Rates || area === OnboardingAuditArea.Companies;

export const isGuestExperience = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.GuestExperience || area === OnboardingAuditArea.Tasks;

export const isAuditUnpaidInvoiceOverview = (audit: OnboardingAuditField) =>
    audit.fieldIdentifier === OnboardingAuditFieldIdentifier.UnpaidInvoiceOverview;
